/**
 * 多版本菜单 B6（老扫码站 U-B6-2）：菜单变了之后购物车怎么办 —— 纯函数（不碰 store / 接口 / 弹窗；用到 store 的流程在 `menuCheck.js`）。
 *
 * 标红一律走老站现成的「过期商品」那条路：`store.state.expireProductList` 里放 `{id: venueProductId, type: 0, source}`，
 * 购物车页按行的 `id`（= venueProductId，`modules/store.js _setProduct`）把那几行显示成删除态、禁用下单按钮，
 * 顾客自己删（`requestMixin.js` 的 `##0##` 弹窗也是这么标的）。**从不清车、从不自动重新下单。**
 * 标记的来历（review 第 1 轮 F2）、合并 / 重判规则、一条标记算不算在某一行上，都在 `cartMarks.js`（没有 import 的纯函数，store 也用）；要用直接从那里引。
 */
import { getLocalizedField, t } from '@/utils/language'
import { EXPIRED_MARK_TYPE, MARK_SOURCE_FALLBACK, MARK_SOURCE_ORDER, cartLinesOf, isMenuCategoryLine } from './cartMarks'

function uniqueStrings(list) {
  if (!Array.isArray(list)) return []
  return list.filter((item, index) => typeof item === 'string' && item !== '' && list.indexOf(item) === index)
}

/** 后端说这一单的菜单已经不适用、它按主菜单校验了（信封 `menuFallback: true`，只有对象信封才可能有）⇒「菜单刚更新」（owner D96） */
export function isMenuFallback(soldOut) {
  return !!soldOut && soldOut.shape === 'keyed' && soldOut.menuFallback === true
}

/**
 * 单店下单被拒的售罄信封（已归一，`soldOutEnvelope.js parseSoldOutEnvelope`）→ 要标出的购物车行。
 * 只有带 id 的信封（请求带了 menuId ⇒ 对象形状，`cartErrorIds`）才标；名字数组（老后端 / 没带 menuId）照今天，一行不标。
 * 来历（review 第 1 轮 F2）：回落拒单 `fallback`（后端按主菜单判的，「菜单刚更新」之后按重新解析出的菜单重判）；别的 `order`（与 `##0##` 标的一样对待）。
 * @returns {Array<{id: String, type: Number, source: String}>}
 */
export function soldOutMarks(soldOut) {
  if (!soldOut || soldOut.shape !== 'keyed') return []
  const source = isMenuFallback(soldOut) ? MARK_SOURCE_FALLBACK : MARK_SOURCE_ORDER
  return uniqueStrings(soldOut.ids).map(id => ({ id, type: EXPIRED_MARK_TYPE, source }))
}

/**
 * 「菜单刚更新」弹窗（owner D96）：文案逐字照搬 kiosk（`concurrent-multi-menu-kiosk` `src/features/language/data/defaults.ts:144-147`，
 * 译文在 `config/defaultSettings.js` 的 en / fr / zh 三份整包里，同样照搬）。一个按钮 `Review order`。
 *
 * owner D2 = A：弹窗在重新解析 + 本地核对**之后**才出。
 * - 做完了（`stillMarked` 是数组）：只列这次被拒、核对之后仍标着的那几行（购物车上显示的名字）——副标题 + 名单；一行都不剩 ⇒ 只有标题；
 * - 重新解析 / 读商品表失败（`stillMarked` 不是数组）：照今天，用后端给的名字（后端已按这一单的语言 / 菜单报名，`RejectionProductNames`）：
 *   有名单 = 副标题 + 名单；没有名单 = 整句。
 * @param {Object} soldOut 归一后的信封
 * @param {String[]} [stillMarked] 核对之后仍标着的行的名字（`stillMarkedNames`）
 */
export function menuUpdatedDialogText(soldOut, stillMarked) {
  const title = t('menu_just_updated', 'The menu was just updated. Please check your cart.')
  const buttonText = t('review_order', 'Review order')
  const subtitle = () => t('menu_updated_items_subtitle', 'These items are no longer available:')
  if (Array.isArray(stillMarked)) {
    const names = uniqueStrings(stillMarked)
    return { title, content: names.length ? [subtitle()].concat(names).join('\n') : '', buttonText }
  }
  const names = uniqueStrings(soldOut && soldOut.names)
  return {
    title,
    content: names.length
      ? [subtitle()].concat(names).join('\n')
      : t('menu_updated_no_names', 'Some items in your cart are no longer available.'),
    buttonText
  }
}

/** 本地核对（D97）标出了行之后的那一句提示：与「菜单刚更新」弹窗的标题同一句 */
export function menuChangedNoticeText() {
  return t('menu_just_updated', 'The menu was just updated. Please check your cart.')
}

/** 商品页重新解析之后还是读不到：一句白话（kiosk 读商品失败那一句，`defaults.ts:58`） */
export function productReadFailedText() {
  return t('item_load_failed_hint', 'We couldn\'t load this item right now. Please try again.')
}

/**
 * get-product-list 的 `productGroup`（这份菜单的**全部**分类，各带 products —— 后端按 `venue_menu_id` 取分类，不看分类 / 商品状态，
 * 与下单菜单闸「有分类、但没有一个在这份菜单里才拒」同口径）→ 这份菜单上的商品 id。
 * 读不出来 / 一件商品都没有 ⇒ null：说不清，不核对（不能因为一次空响应把整车都标掉）。
 * @returns {Set<String>|null}
 */
export function menuProductIds(productGroups) {
  if (!Array.isArray(productGroups)) return null
  const ids = new Set()
  productGroups.forEach(group => {
    const products = group && Array.isArray(group.products) ? group.products : []
    products.forEach(product => {
      const id = product && product.id
      if ((typeof id === 'string' && id) || typeof id === 'number') ids.add(String(id))
    })
  })
  return ids.size ? ids : null
}

/**
 * 这份菜单上**能下单**的商品（review 第 1 轮 F2，owner 托管 P2 的判据）：列在某个分类里、`status === 1` ——
 * 与首页把商品显示成能点（不是「Sold Out」遮罩）的判据同口径（`venue-home/index.vue` `:maskDisplay="!item.status"`；后端商品只有 0 / 1，
 * 库存不够时 get-product-list 已经把 status 改成 0）。同一件商品出现在几个分类里，**每一处**都得是 1；拿不准就当不能下单（标记留着）。
 * @returns {Set<String>}
 */
export function orderableProductIds(productGroups) {
  const verdict = new Map()
  ;(Array.isArray(productGroups) ? productGroups : []).forEach(group => {
    const products = group && Array.isArray(group.products) ? group.products : []
    products.forEach(product => {
      const id = product && product.id
      if (!((typeof id === 'string' && id) || typeof id === 'number')) return
      const key = String(id)
      const orderable = product.status === 1
      verdict.set(key, verdict.has(key) ? verdict.get(key) && orderable : orderable)
    })
  })
  return new Set([...verdict.keys()].filter(key => verdict.get(key)))
}

/** 购物车行上显示的名字（与 `cart/component/ItemsContent` 的 `localizedName` 同一个写法） */
function cartLineName(line) {
  return line.upgradeProductName || getLocalizedField(line, 'name') || line.name
}

/**
 * 「菜单刚更新」弹窗的名单（D2 = A）：这次信封里的 id、核对之后仍有标记、这家店顾客能删的行 → 购物车上显示的名字（去重）。
 * @param {Object} state root state（`productList` / `expireProductList` / `venueId`）
 * @param {String[]} rejectedIds 这次信封里的 id
 * @returns {String[]}
 */
export function stillMarkedNames(state, rejectedIds) {
  const rejected = new Set((Array.isArray(rejectedIds) ? rejectedIds : []).map(String))
  const marked = new Set((Array.isArray(state && state.expireProductList) ? state.expireProductList : []).map(mark => mark && String(mark.id)))
  const names = cartLinesOf(state && state.productList, state && state.venueId)
    .filter(line => rejected.has(String(line.id)) && marked.has(String(line.id)))
    .map(cartLineName)
  return uniqueStrings(names)
}

/**
 * D97：购物车里不在新菜单上的行（只看从菜单分类加的行）→ 去重后的商品 id。
 * @param {Array} productList 购物车（root state productList）
 * @param {String} venueId 这家店
 * @param {Set<String>} productIds 新菜单上的商品（`menuProductIds`）
 */
export function linesOffMenu(productList, venueId, productIds) {
  if (!productIds) return []
  const ids = cartLinesOf(productList, venueId)
    .filter(line => isMenuCategoryLine(line, venueId) && !productIds.has(String(line.id)))
    .map(line => String(line.id))
  return uniqueStrings(ids)
}
